import { useEffect, useRef } from 'react'
import { useApplicationStore } from 'hooks/application'

/**
 * Whether the hub a request was made for is still the one the page shows. A settings page keeps
 * itself between hubs, so an answer that comes back after you've switched hub belongs to the hub
 * before: check `stillShown(hub)` before letting it change the page.
 */
export const useShownHub = (projectId: string): ((hub: string) => boolean) => {
  const shown = useRef(projectId)
  useEffect(() => {
    shown.current = projectId
  }, [projectId])
  return (hub: string) => hub === shown.current
}

/**
 * Says an action failed: with the API's message while the hub it was for is on show; after a switch to
 * another hub, with `elsewhere`, a message naming that hub, so the failure isn't read as the hub shown
 * since (and isn't lost).
 */
export const useHubFailure = (projectId: string): ((error: unknown, hub: string, elsewhere: string) => void) => {
  const stillShown = useShownHub(projectId)
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  return (error: unknown, hub: string, elsewhere: string) => {
    if (stillShown(hub)) displayErrorMessage(error)
    else setSnackbarMessage({ status: 'error', message: elsewhere })
  }
}
